import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, Tag, Image as ImageIcon, Settings, Trash2, Plus, Save, ExternalLink, Wrench, FileText, ArrowUp, ArrowDown, Star } from "lucide-react";
import { toast, Toaster } from "sonner";

async function moveItem(collection, items, id, direction) {
  const idx = items.findIndex((x) => x.id === id);
  if (idx === -1) return null;
  const target = direction === "up" ? idx - 1 : idx + 1;
  if (target < 0 || target >= items.length) return null;
  const newOrder = [...items];
  [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
  await api.post(`/${collection}/reorder`, { ids: newOrder.map((x) => x.id) });
  return true;
}

const tabs = [
  { key: "offers", label: "Offerte", icon: Tag },
  { key: "gallery", label: "Galleria", icon: ImageIcon },
  { key: "services", label: "Servizi", icon: Wrench },
  { key: "reviews", label: "Recensioni", icon: Star },
  { key: "content", label: "Contenuti", icon: FileText },
  { key: "info", label: "Info Contatti", icon: Settings },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("offers");
  const [offers, setOffers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [siteInfo, setSiteInfo] = useState(null);

  const loadAll = async () => {
    const [o, g, s, r, info] = await Promise.all([
      api.get("/offers"),
      api.get("/gallery"),
      api.get("/services"),
      api.get("/reviews"),
      api.get("/site-info"),
    ]);
    setOffers(o.data);
    setGallery(g.data);
    setServices(s.data);
    setReviews(r.data);
    setSiteInfo(info.data);
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-palumbo-black text-white flex">
      <Toaster theme="dark" position="top-right" />
      <aside className="w-64 bg-palumbo-dark border-r border-white/10 hidden md:flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 bg-palumbo-copper flex items-center justify-center font-display font-black text-black">P</span>
            <div>
              <div className="font-display text-sm font-extrabold uppercase">Palumbo</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-palumbo-copper">Admin</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-6">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`w-full flex items-center gap-3 px-6 py-3 text-sm uppercase tracking-widest font-bold transition-colors ${
                  tab === t.key
                    ? "bg-palumbo-copper/10 text-palumbo-copper border-l-2 border-palumbo-copper"
                    : "text-neutral-400 hover:text-white border-l-2 border-transparent"
                }`}
              >
                <Icon size={16} strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="p-6 border-t border-white/10 space-y-3">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-neutral-400 hover:text-palumbo-copper uppercase tracking-widest">
            <ExternalLink size={14} /> Vai al sito
          </a>
          <button onClick={handleLogout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-xs text-neutral-400 hover:text-palumbo-copper uppercase tracking-widest">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl uppercase tracking-tighter">Dashboard</h1>
            <p className="text-sm text-neutral-400 mt-1">Benvenuto, {user?.name || user?.email}</p>
          </div>
          <div className="flex md:hidden gap-2 overflow-x-auto">
            {tabs.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`text-[10px] uppercase tracking-widest px-3 py-2 whitespace-nowrap ${tab===t.key?'bg-palumbo-copper text-white':'border border-white/15 text-neutral-400'}`}>{t.label}</button>
            ))}
          </div>
        </div>

        {tab === "offers" && <OffersPanel offers={offers} reload={loadAll} />}
        {tab === "gallery" && <GalleryPanel items={gallery} reload={loadAll} />}
        {tab === "services" && <ServicesPanel services={services} reload={loadAll} />}
        {tab === "reviews" && <ReviewsPanel reviews={reviews} reload={loadAll} />}
        {tab === "content" && siteInfo && <ContentPanel info={siteInfo} reload={loadAll} />}
        {tab === "info" && siteInfo && <ContactInfoPanel info={siteInfo} reload={loadAll} />}
      </main>
    </div>
  );
}

/* ---------------- OFFERS ---------------- */
function OffersPanel({ offers, reload }) {
  const [form, setForm] = useState({ title: "", description: "", discount: "", active: true });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/offers", form);
      setForm({ title: "", description: "", discount: "", active: true });
      await reload();
      toast.success("Offerta creata");
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare l'offerta?")) return;
    await api.delete(`/offers/${id}`);
    await reload();
    toast.success("Offerta eliminata");
  };

  const toggle = async (o) => {
    await api.put(`/offers/${o.id}`, { active: !o.active });
    await reload();
  };

  const move = async (id, dir) => {
    const ok = await moveItem("offers", offers, id, dir);
    if (ok) await reload();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="bg-palumbo-dark border border-white/10 p-6 space-y-4">
        <h2 className="font-display font-bold text-lg uppercase flex items-center gap-2"><Plus size={18} className="text-palumbo-copper" /> Nuova Offerta</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input required placeholder="Titolo" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="md:col-span-2 admin-input" />
          <input placeholder="Sconto (es. -20%)" value={form.discount} onChange={(e) => setForm({...form, discount: e.target.value})} className="admin-input" />
        </div>
        <textarea required placeholder="Descrizione" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full admin-input" />
        <button type="submit" className="btn-primary"><Plus size={14} /> Crea Offerta</button>
      </form>

      <div className="space-y-3">
        <h3 className="text-xs uppercase tracking-[0.25em] text-neutral-400">Offerte ({offers.length})</h3>
        {offers.map((o, i) => (
          <div key={o.id} className="bg-palumbo-dark border border-white/10 p-5 flex items-start gap-4">
            <div className="flex flex-col gap-1">
              <button onClick={() => move(o.id, "up")} disabled={i===0} className="btn-arrow" title="Sposta su"><ArrowUp size={14} /></button>
              <button onClick={() => move(o.id, "down")} disabled={i===offers.length-1} className="btn-arrow" title="Sposta giù"><ArrowDown size={14} /></button>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h4 className="font-display font-bold uppercase">{o.title}</h4>
                {o.discount && <span className="text-[10px] bg-palumbo-copper text-white px-2 py-0.5 uppercase tracking-widest">{o.discount}</span>}
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${o.active ? "bg-green-500/20 text-green-400" : "bg-neutral-500/20 text-neutral-400"}`}>
                  {o.active ? "Attiva" : "Disattiva"}
                </span>
              </div>
              <p className="text-sm text-neutral-400">{o.description}</p>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => toggle(o)} className="btn-sm">{o.active ? "Disattiva" : "Attiva"}</button>
              <button onClick={() => remove(o.id)} className="btn-sm-danger"><Trash2 size={12} /> Elimina</button>
            </div>
          </div>
        ))}
        {offers.length === 0 && <p className="text-sm text-neutral-500">Nessuna offerta presente.</p>}
      </div>
      <FormStyles />
    </div>
  );
}

/* ---------------- GALLERY ---------------- */
function GalleryPanel({ items, reload }) {
  const [form, setForm] = useState({ title: "", category: "Custom", image_url: "", description: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/gallery", form);
      setForm({ title: "", category: "Custom", image_url: "", description: "" });
      await reload();
      toast.success("Foto aggiunta");
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/gallery/${id}`);
    await reload();
    toast.success("Eliminata");
  };

  const move = async (id, dir) => {
    const ok = await moveItem("gallery", items, id, dir);
    if (ok) await reload();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="bg-palumbo-dark border border-white/10 p-6 space-y-4">
        <h2 className="font-display font-bold text-lg uppercase flex items-center gap-2"><Plus size={18} className="text-palumbo-copper" /> Aggiungi Foto</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Titolo" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="admin-input" />
          <input placeholder="Categoria" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="admin-input" />
        </div>
        <input required placeholder="URL Immagine (https://...)" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="w-full admin-input" />
        <textarea placeholder="Descrizione (opzionale)" rows={2} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full admin-input" />
        <button type="submit" className="btn-primary"><Plus size={14} /> Aggiungi</button>
      </form>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">Galleria ({items.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((g, i) => (
            <div key={g.id} className="bg-palumbo-dark border border-white/10 overflow-hidden">
              <img src={g.image_url} alt={g.title} className="w-full h-44 object-cover" />
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-palumbo-copper mb-1">{g.category}</div>
                <div className="font-display font-bold uppercase text-sm mb-3">{g.title}</div>
                <div className="flex gap-2 mb-2">
                  <button onClick={() => move(g.id, "up")} disabled={i===0} className="btn-arrow flex-1 justify-center" title="Sposta su"><ArrowUp size={14} /></button>
                  <button onClick={() => move(g.id, "down")} disabled={i===items.length-1} className="btn-arrow flex-1 justify-center" title="Sposta giù"><ArrowDown size={14} /></button>
                </div>
                <button onClick={() => remove(g.id)} className="w-full btn-sm-danger justify-center"><Trash2 size={12} /> Elimina</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <FormStyles />
    </div>
  );
}

/* ---------------- SERVICES ---------------- */
const ICON_OPTIONS = ["hammer", "zap", "car", "spray-can", "wrench"];

function ServicesPanel({ services, reload }) {
  const [form, setForm] = useState({ title: "", description: "", icon: "wrench", image_url: "", order: 0 });
  const [editing, setEditing] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/services/${editing}`, form);
        toast.success("Servizio aggiornato");
        setEditing(null);
      } else {
        await api.post("/services", form);
        toast.success("Servizio creato");
      }
      setForm({ title: "", description: "", icon: "wrench", image_url: "", order: 0 });
      await reload();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il servizio?")) return;
    await api.delete(`/services/${id}`);
    await reload();
    toast.success("Eliminato");
  };

  const edit = (svc) => {
    setEditing(svc.id);
    setForm({ title: svc.title, description: svc.description, icon: svc.icon, image_url: svc.image_url, order: svc.order });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => {
    setEditing(null);
    setForm({ title: "", description: "", icon: "wrench", image_url: "", order: 0 });
  };

  const move = async (id, dir) => {
    const ok = await moveItem("services", services, id, dir);
    if (ok) await reload();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="bg-palumbo-dark border border-white/10 p-6 space-y-4">
        <h2 className="font-display font-bold text-lg uppercase flex items-center gap-2">
          {editing ? <><Save size={18} className="text-palumbo-copper" /> Modifica Servizio</> : <><Plus size={18} className="text-palumbo-copper" /> Nuovo Servizio</>}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Titolo" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="admin-input" />
          <select value={form.icon} onChange={(e) => setForm({...form, icon: e.target.value})} className="admin-input">
            {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
        <input required placeholder="URL Immagine" value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} className="w-full admin-input" />
        <textarea required placeholder="Descrizione" rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="w-full admin-input" />
        <input type="number" placeholder="Ordine" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})} className="w-32 admin-input" />
        <div className="flex gap-3">
          <button type="submit" className="btn-primary">{editing ? <><Save size={14} /> Salva</> : <><Plus size={14} /> Crea</>}</button>
          {editing && <button type="button" onClick={cancel} className="btn-sm">Annulla</button>}
        </div>
      </form>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">Servizi ({services.length})</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {services.map((s, i) => (
            <div key={s.id} className="bg-palumbo-dark border border-white/10 overflow-hidden flex flex-col md:flex-row">
              <img src={s.image_url} alt={s.title} className="w-full md:w-40 h-40 object-cover" />
              <div className="p-4 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-widest text-palumbo-copper mb-1">#{i+1} • {s.icon}</div>
                <div className="font-display font-bold uppercase mb-2">{s.title}</div>
                <p className="text-xs text-neutral-400 line-clamp-2 mb-3">{s.description}</p>
                <div className="mt-auto flex gap-2 flex-wrap">
                  <button onClick={() => move(s.id, "up")} disabled={i===0} className="btn-arrow" title="Sposta su"><ArrowUp size={14} /></button>
                  <button onClick={() => move(s.id, "down")} disabled={i===services.length-1} className="btn-arrow" title="Sposta giù"><ArrowDown size={14} /></button>
                  <button onClick={() => edit(s)} className="btn-sm">Modifica</button>
                  <button onClick={() => remove(s.id)} className="btn-sm-danger"><Trash2 size={12} /> Elimina</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <FormStyles />
    </div>
  );
}

/* ---------------- CONTENT (Hero, About, Sections, Stats, Footer) ---------------- */
/* ---------------- REVIEWS ---------------- */
function ReviewsPanel({ reviews, reload }) {
  const [form, setForm] = useState({ name: "", city: "", rating: 5, text: "", order: 0 });
  const [editing, setEditing] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/reviews/${editing}`, form);
        toast.success("Recensione aggiornata");
        setEditing(null);
      } else {
        await api.post("/reviews", form);
        toast.success("Recensione creata");
      }
      setForm({ name: "", city: "", rating: 5, text: "", order: 0 });
      await reload();
    } catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la recensione?")) return;
    await api.delete(`/reviews/${id}`);
    await reload();
    toast.success("Recensione eliminata");
  };

  const edit = (r) => {
    setEditing(r.id);
    setForm({ name: r.name, city: r.city, rating: r.rating, text: r.text, order: r.order });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => {
    setEditing(null);
    setForm({ name: "", city: "", rating: 5, text: "", order: 0 });
  };

  const move = async (id, dir) => {
    const ok = await moveItem("reviews", reviews, id, dir);
    if (ok) await reload();
  };

  return (
    <div className="space-y-8">
      <form onSubmit={submit} className="bg-palumbo-dark border border-white/10 p-6 space-y-4">
        <h2 className="font-display font-bold text-lg uppercase flex items-center gap-2">
          {editing ? <><Save size={18} className="text-palumbo-copper" /> Modifica Recensione</> : <><Plus size={18} className="text-palumbo-copper" /> Nuova Recensione</>}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input required placeholder="Nome (es. Marco B.)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="admin-input md:col-span-1" />
          <input placeholder="Città (opzionale)" value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className="admin-input md:col-span-1" />
          <select value={form.rating} onChange={(e) => setForm({...form, rating: parseInt(e.target.value)})} className="admin-input md:col-span-1">
            {[5,4,3,2,1].map((n) => <option key={n} value={n}>{"★".repeat(n)}{"☆".repeat(5-n)} — {n} stelle</option>)}
          </select>
        </div>
        <textarea required placeholder="Testo della recensione" rows={4} value={form.text} onChange={(e) => setForm({...form, text: e.target.value})} className="w-full admin-input" />
        <div className="flex gap-3">
          <button type="submit" className="btn-primary">{editing ? <><Save size={14} /> Salva</> : <><Plus size={14} /> Crea</>}</button>
          {editing && <button type="button" onClick={cancel} className="btn-sm">Annulla</button>}
        </div>
      </form>

      <div>
        <h3 className="text-xs uppercase tracking-[0.25em] text-neutral-400 mb-4">Recensioni ({reviews.length})</h3>
        <div className="space-y-3">
          {reviews.map((r, i) => (
            <div key={r.id} className="bg-palumbo-dark border border-white/10 p-5 flex items-start gap-4">
              <div className="flex flex-col gap-1">
                <button onClick={() => move(r.id, "up")} disabled={i===0} className="btn-arrow" title="Sposta su"><ArrowUp size={14} /></button>
                <button onClick={() => move(r.id, "down")} disabled={i===reviews.length-1} className="btn-arrow" title="Sposta giù"><ArrowDown size={14} /></button>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h4 className="font-display font-bold uppercase">{r.name}</h4>
                  {r.city && <span className="text-[10px] bg-palumbo-copper/20 text-palumbo-copper px-2 py-0.5 uppercase tracking-widest">{r.city}</span>}
                  <span className="text-palumbo-copper text-xs">{"★".repeat(r.rating)}{"☆".repeat(5-r.rating)}</span>
                </div>
                <p className="text-sm text-neutral-400 italic">&ldquo;{r.text}&rdquo;</p>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => edit(r)} className="btn-sm">Modifica</button>
                <button onClick={() => remove(r.id)} className="btn-sm-danger"><Trash2 size={12} /> Elimina</button>
              </div>
            </div>
          ))}
          {reviews.length === 0 && <p className="text-sm text-neutral-500">Nessuna recensione presente.</p>}
        </div>
      </div>
      <FormStyles />
    </div>
  );
}


function ContentPanel({ info, reload }) {
  const [form, setForm] = useState(info);

  useEffect(() => { setForm(info); }, [info]);

  const save = async () => {
    try {
      await api.put("/site-info", form);
      await reload();
      toast.success("Contenuti aggiornati");
    } catch { toast.error("Errore"); }
  };

  const setNested = (section, key, value) => {
    setForm({ ...form, [section]: { ...form[section], [key]: value } });
  };

  const setHeroStat = (idx, key, value) => {
    const arr = [...(form.hero_stats || [])];
    arr[idx] = { ...arr[idx], [key]: value };
    setForm({ ...form, hero_stats: arr });
  };

  const setAboutStat = (idx, key, value) => {
    const arr = [...(form.about.stats || [])];
    arr[idx] = { ...arr[idx], [key]: value };
    setForm({ ...form, about: { ...form.about, stats: arr } });
  };

  const moveSectionOrder = (idx, dir) => {
    const arr = [...(form.section_order || ["services","offers","gallery","about","contact"])];
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    setForm({ ...form, section_order: arr });
  };

  const toggleVisibility = (key) => {
    const cur = form.section_visibility || {};
    const isVisible = cur[key] !== false;
    setForm({ ...form, section_visibility: { ...cur, [key]: !isVisible } });
  };

  const handleHeroImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 5MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setNested("hero", "image_url", reader.result);
      toast.success("Immagine caricata — ricordati di salvare");
    };
    reader.readAsDataURL(file);
  };

  const SECTION_LABELS = {
    services: "Servizi",
    offers: "Offerte",
    gallery: "Galleria",
    reviews: "Recensioni",
    about: "Chi Siamo",
    contact: "Contatti",
  };

  if (!form) return null;

  return (
    <div className="space-y-8 max-w-4xl pb-12">
      <button onClick={save} className="btn-primary sticky top-4 z-10 shadow-2xl"><Save size={14} /> Salva tutti i contenuti</button>

      {/* SECTION ORDER + VISIBILITY */}
      <Section title="Ordine e visibilità delle sezioni">
        <p className="text-xs text-neutral-400 mb-3">Usa le frecce per riordinare le sezioni. Il pulsante &quot;Attiva/Nascondi&quot; permette di nascondere una sezione dalla home senza cancellare i dati.</p>
        <div className="space-y-2">
          {(form.section_order || ["services","offers","gallery","reviews","about","contact"]).map((key, i, arr) => {
            const visible = (form.section_visibility || {})[key] !== false;
            return (
              <div key={key} className={`flex items-center gap-3 border px-4 py-3 transition-colors ${visible ? "bg-black/30 border-white/10" : "bg-black/50 border-white/5 opacity-60"}`}>
                <div className={`w-6 h-6 flex items-center justify-center text-xs font-black ${visible ? "bg-palumbo-copper text-black" : "bg-neutral-700 text-neutral-400"}`}>{i+1}</div>
                <div className="flex-1 font-display font-bold uppercase tracking-tight flex items-center gap-2">
                  {SECTION_LABELS[key] || key}
                  {!visible && <span className="text-[9px] uppercase tracking-widest text-neutral-500 border border-neutral-600 px-1.5 py-0.5">Nascosta</span>}
                </div>
                <button type="button" onClick={() => toggleVisibility(key)}
                  className={`text-[10px] uppercase tracking-widest px-3 py-2 border transition-colors ${visible ? "border-white/15 text-white hover:border-red-500 hover:text-red-500" : "border-palumbo-copper text-palumbo-copper hover:bg-palumbo-copper hover:text-white"}`}>
                  {visible ? "Nascondi" : "Attiva"}
                </button>
                <button type="button" onClick={() => moveSectionOrder(i, "up")} disabled={i===0} className="btn-arrow" title="Sposta su"><ArrowUp size={14} /></button>
                <button type="button" onClick={() => moveSectionOrder(i, "down")} disabled={i===arr.length-1} className="btn-arrow" title="Sposta giù"><ArrowDown size={14} /></button>
              </div>
            );
          })}
        </div>
      </Section>

      {/* HERO */}
      <Section title="Sezione Hero (Home)">
        <Field label="Badge (sopra il titolo)" value={form.hero?.badge || ""} onChange={(v) => setNested("hero", "badge", v)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Titolo riga 1" value={form.hero?.title_line1 || ""} onChange={(v) => setNested("hero", "title_line1", v)} />
          <Field label="Parola evidenziata" value={form.hero?.title_highlight || ""} onChange={(v) => setNested("hero", "title_highlight", v)} />
          <Field label="Resto del titolo" value={form.hero?.title_line2 || ""} onChange={(v) => setNested("hero", "title_line2", v)} />
        </div>
        <Field label="Sottotitolo" textarea rows={3} value={form.hero?.subtitle || ""} onChange={(v) => setNested("hero", "subtitle", v)} />
        <Field label="URL immagine di sfondo" value={form.hero?.image_url || ""} onChange={(v) => setNested("hero", "image_url", v)} />
        <div className="flex items-center gap-3">
          <label className="btn-sm cursor-pointer">
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={handleHeroImageUpload} className="hidden" />
            📤 Carica un'immagine dal tuo dispositivo
          </label>
          <span className="text-[10px] text-neutral-500">PNG/JPG/WEBP – max 5MB</span>
        </div>
        {form.hero?.image_url && <img src={form.hero.image_url} alt="hero" className="w-full max-w-md h-32 object-cover border border-white/10" />}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Testo bottone primario" value={form.hero?.cta_primary || ""} onChange={(v) => setNested("hero", "cta_primary", v)} />
          <Field label="Testo bottone secondario" value={form.hero?.cta_secondary || ""} onChange={(v) => setNested("hero", "cta_secondary", v)} />
        </div>
      </Section>

      {/* HERO STATS */}
      <Section title="Statistiche Home (4 numeri)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(form.hero_stats || []).map((s, i) => (
            <div key={i} className="border border-white/10 p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-widest text-palumbo-copper">Stat #{i+1}</div>
              <Field label="Valore (es. 15+)" value={s.value || ""} onChange={(v) => setHeroStat(i, "value", v)} />
              <Field label="Etichetta" value={s.label || ""} onChange={(v) => setHeroStat(i, "label", v)} />
            </div>
          ))}
        </div>
      </Section>

      {/* SECTIONS */}
      <Section title="Titoli e descrizioni delle sezioni">
        <SubGroup label="Servizi">
          <Field label="Badge" value={form.sections?.services_badge || ""} onChange={(v) => setNested("sections", "services_badge", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Titolo" value={form.sections?.services_title || ""} onChange={(v) => setNested("sections", "services_title", v)} />
            <Field label="Parola evidenziata" value={form.sections?.services_title_highlight || ""} onChange={(v) => setNested("sections", "services_title_highlight", v)} />
          </div>
          <Field label="Descrizione" textarea rows={2} value={form.sections?.services_description || ""} onChange={(v) => setNested("sections", "services_description", v)} />
        </SubGroup>

        <SubGroup label="Offerte">
          <Field label="Badge" value={form.sections?.offers_badge || ""} onChange={(v) => setNested("sections", "offers_badge", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Titolo" value={form.sections?.offers_title || ""} onChange={(v) => setNested("sections", "offers_title", v)} />
            <Field label="Parola evidenziata" value={form.sections?.offers_title_highlight || ""} onChange={(v) => setNested("sections", "offers_title_highlight", v)} />
          </div>
          <Field label="Descrizione" textarea rows={2} value={form.sections?.offers_description || ""} onChange={(v) => setNested("sections", "offers_description", v)} />
        </SubGroup>

        <SubGroup label="Galleria">
          <Field label="Badge" value={form.sections?.gallery_badge || ""} onChange={(v) => setNested("sections", "gallery_badge", v)} />
          <Field label="Titolo" value={form.sections?.gallery_title || ""} onChange={(v) => setNested("sections", "gallery_title", v)} />
          <Field label="Descrizione" textarea rows={2} value={form.sections?.gallery_description || ""} onChange={(v) => setNested("sections", "gallery_description", v)} />
        </SubGroup>

        <SubGroup label="Recensioni">
          <Field label="Badge" value={form.sections?.reviews_badge || ""} onChange={(v) => setNested("sections", "reviews_badge", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Titolo" value={form.sections?.reviews_title || ""} onChange={(v) => setNested("sections", "reviews_title", v)} />
            <Field label="Parola evidenziata" value={form.sections?.reviews_title_highlight || ""} onChange={(v) => setNested("sections", "reviews_title_highlight", v)} />
          </div>
          <Field label="Descrizione" textarea rows={2} value={form.sections?.reviews_description || ""} onChange={(v) => setNested("sections", "reviews_description", v)} />
        </SubGroup>

        <SubGroup label="Contatti">
          <Field label="Badge" value={form.sections?.contact_badge || ""} onChange={(v) => setNested("sections", "contact_badge", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Titolo riga 1" value={form.sections?.contact_title_line1 || ""} onChange={(v) => setNested("sections", "contact_title_line1", v)} />
            <Field label="Parola evidenziata" value={form.sections?.contact_title_highlight || ""} onChange={(v) => setNested("sections", "contact_title_highlight", v)} />
          </div>
          <Field label="Descrizione" textarea rows={2} value={form.sections?.contact_description || ""} onChange={(v) => setNested("sections", "contact_description", v)} />
        </SubGroup>
      </Section>

      {/* ABOUT */}
      <Section title="Sezione Chi Siamo">
        <Field label="Badge" value={form.about?.badge || ""} onChange={(v) => setNested("about", "badge", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Titolo riga 1" value={form.about?.title_line1 || ""} onChange={(v) => setNested("about", "title_line1", v)} />
          <Field label="Parola evidenziata" value={form.about?.title_highlight || ""} onChange={(v) => setNested("about", "title_highlight", v)} />
        </div>
        <Field label="Paragrafo 1" textarea rows={4} value={form.about?.paragraph1 || ""} onChange={(v) => setNested("about", "paragraph1", v)} />
        <Field label="Paragrafo 2" textarea rows={3} value={form.about?.paragraph2 || ""} onChange={(v) => setNested("about", "paragraph2", v)} />
        <Field label="URL immagine" value={form.about?.image_url || ""} onChange={(v) => setNested("about", "image_url", v)} />
        {form.about?.image_url && <img src={form.about.image_url} alt="about" className="w-full max-w-xs h-40 object-cover border border-white/10" />}
        <Field label="Etichetta sopra immagine" value={form.about?.badge_overlay || ""} onChange={(v) => setNested("about", "badge_overlay", v)} />

        <div className="pt-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-widest text-neutral-400 mb-3">Statistiche Chi Siamo</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(form.about?.stats || []).map((s, i) => (
              <div key={i} className="border border-white/10 p-4 space-y-3">
                <Field label={`Stat #${i+1} - Valore`} value={s.value || ""} onChange={(v) => setAboutStat(i, "value", v)} />
                <Field label="Etichetta" value={s.label || ""} onChange={(v) => setAboutStat(i, "label", v)} />
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <Section title="Footer">
        <Field label="Descrizione footer" textarea rows={3} value={form.footer_description || ""} onChange={(v) => setForm({ ...form, footer_description: v })} />
        <Field label="Tagline" value={form.tagline || ""} onChange={(v) => setForm({ ...form, tagline: v })} />
      </Section>

      <button onClick={save} className="btn-primary"><Save size={14} /> Salva tutti i contenuti</button>
      <FormStyles />
    </div>
  );
}

function ContactInfoPanel({ info, reload }) {
  const [form, setForm] = useState(info);
  useEffect(() => { setForm(info); }, [info]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/site-info", form);
      await reload();
      toast.success("Info aggiornate");
    } catch { toast.error("Errore"); }
  };

  const fields = [
    { k: "phone", label: "Telefono" },
    { k: "whatsapp", label: "WhatsApp (solo numeri, es. 393331234567)" },
    { k: "email", label: "Email" },
    { k: "address", label: "Indirizzo" },
    { k: "instagram", label: "URL Instagram" },
    { k: "tiktok", label: "URL TikTok" },
  ];

  return (
    <form onSubmit={save} className="bg-palumbo-dark border border-white/10 p-6 space-y-4 max-w-2xl">
      <h2 className="font-display font-bold text-lg uppercase">Informazioni di contatto</h2>
      {fields.map((f) => (
        <Field key={f.k} label={f.label} value={form[f.k] || ""} onChange={(v) => setForm({ ...form, [f.k]: v })} />
      ))}
      <button type="submit" className="btn-primary"><Save size={14} /> Salva modifiche</button>
      <FormStyles />
    </form>
  );
}

/* ---------------- helpers ---------------- */
function Section({ title, children }) {
  return (
    <div className="bg-palumbo-dark border border-white/10 p-6 space-y-4">
      <h3 className="font-display font-bold text-lg uppercase tracking-tight text-palumbo-copper">{title}</h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function SubGroup({ label, children }) {
  return (
    <div className="border border-white/5 bg-black/30 p-4 space-y-3">
      <div className="text-[10px] uppercase tracking-widest text-palumbo-copper">{label}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, textarea = false, rows = 2 }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-neutral-400 block mb-2">{label}</label>
      {textarea ? (
        <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className="w-full admin-input" />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full admin-input" />
      )}
    </div>
  );
}

function FormStyles() {
  return (
    <style>{`
      .admin-input { background:#0A0A0A; border:1px solid rgba(255,255,255,0.15); padding:12px 16px; font-size:13px; color:#fff; outline:none; transition:border-color .2s; }
      .admin-input:focus { border-color:#D95A2B; }
      .btn-primary { display:inline-flex; align-items:center; gap:8px; background:#D95A2B; color:#fff; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; padding:12px 24px; font-size:12px; transition:background .2s; }
      .btn-primary:hover { background:#BF4A26; }
      .btn-sm { display:inline-flex; align-items:center; gap:4px; border:1px solid rgba(255,255,255,0.15); color:#fff; font-size:10px; text-transform:uppercase; letter-spacing:0.1em; padding:8px 12px; }
      .btn-sm:hover { border-color:#D95A2B; color:#D95A2B; }
      .btn-sm-danger { display:inline-flex; align-items:center; gap:4px; border:1px solid rgba(255,255,255,0.15); color:#fff; font-size:10px; text-transform:uppercase; letter-spacing:0.1em; padding:8px 12px; }
      .btn-sm-danger:hover { border-color:#ef4444; color:#ef4444; }
      .btn-arrow { display:inline-flex; align-items:center; gap:4px; border:1px solid rgba(255,255,255,0.15); color:#fff; padding:8px 10px; transition:all .2s; }
      .btn-arrow:hover:not(:disabled) { border-color:#D95A2B; color:#D95A2B; background:rgba(217,90,43,0.05); }
      .btn-arrow:disabled { opacity:0.3; cursor:not-allowed; }
    `}</style>
  );
}
